/**
 * Debrief — post-sortie results screen.
 *
 * Shows accuracy, avg time, best streak, score, weak contacts, and XP earned.
 */

import type { SortieResult } from './types'

interface DebriefProps {
  result: SortieResult
  onFlyAgain: () => void
  onBackToArcade: () => void
}

function getGradeMessage(accuracy: number): string {
  if (accuracy >= 95) return 'Outstanding sortie, pilot. Command is impressed.'
  if (accuracy >= 85) return 'Solid flying. Keep this up and you\'ll make ace.'
  if (accuracy >= 70) return 'Good run. A few contacts slipped through — drill those.'
  if (accuracy >= 60) return 'Mission complete, but there\'s work to do. Hit the sim.'
  return 'Tough sortie. Review the targets below and fly again.'
}

export default function Debrief({ result, onFlyAgain, onBackToArcade }: DebriefProps) {
  const accuracyPct = Math.round(result.accuracy * 100)
  const avgTimeSec = (result.avgResponseTimeMs / 1000).toFixed(1)

  // Grade based on accuracy
  let grade = 'S'
  let gradeColor = '#00FF41'
  if (accuracyPct < 60) { grade = 'D'; gradeColor = '#FF3333' }
  else if (accuracyPct < 70) { grade = 'C'; gradeColor = '#FF9933' }
  else if (accuracyPct < 80) { grade = 'B'; gradeColor = '#FFDD33' }
  else if (accuracyPct < 95) { grade = 'A'; gradeColor = '#00FF41' }

  // Count mastery level-ups
  const levelUps = result.masteryUpdates?.filter(
    (u) => u.newLevel > u.previousLevel
  ).length ?? 0

  return (
    <div className="min-h-screen bg-[#020617] text-[#00FF41] font-mono flex flex-col items-center justify-center p-6">
      {/* Scanline overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-50 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, transparent, transparent 50%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.3))',
          backgroundSize: '100% 4px',
        }}
      />

      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1
            className="text-3xl font-bold tracking-widest"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            MISSION DEBRIEF
          </h1>
          <div className="text-[#00FF4166] text-sm tracking-[0.3em]">
            SORTIE COMPLETE
          </div>
        </div>

        {/* Grade */}
        <div className="text-center space-y-2">
          <div
            className="text-7xl font-bold inline-block px-6 py-2 border-2"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              color: gradeColor,
              borderColor: gradeColor,
              boxShadow: `0 0 20px ${gradeColor}40`,
            }}
          >
            {grade}
          </div>
          <div className="text-sm opacity-70 max-w-xs mx-auto">
            {getGradeMessage(accuracyPct)}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="HIT RATE" value={`${accuracyPct}%`} />
          <StatCard label="AVG TIME" value={`${avgTimeSec}s`} />
          <StatCard label="BEST STREAK" value={`${result.bestStreak}`} />
          <StatCard label="SCORE" value={result.totalScore.toLocaleString()} />
        </div>

        {/* XP earned — always show */}
        <div className="text-center border border-[#00FF4140] bg-[#00FF41]/5 py-3 space-y-1">
          <div className="text-xs opacity-60 tracking-widest">XP EARNED</div>
          <div
            className="text-3xl font-bold"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            +{result.xpEarned ?? 0}
          </div>
          {levelUps > 0 && (
            <div className="text-xs text-[#00CCAA]">
              {levelUps} {levelUps === 1 ? 'fact' : 'facts'} leveled up
            </div>
          )}
        </div>

        {/* Weak contacts */}
        {result.weakContacts.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs tracking-widest opacity-60">
              CONTACTS FOR REVIEW ({result.weakContacts.length})
            </div>
            <div className="text-[10px] opacity-40 -mt-1">
              These will come up more often until you nail them.
            </div>
            <div className="border border-amber-500/30 bg-amber-500/5 p-3 space-y-1 max-h-40 overflow-y-auto">
              {result.weakContacts.map((c, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-amber-400">{c.prompt}</span>
                  <span className="opacity-60">
                    = {c.correctAnswer}
                    {c.attempts > 1 && (
                      <span className="text-amber-500 ml-2">
                        ({c.attempts} tries)
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onFlyAgain}
            className="flex-1 py-3 font-bold tracking-widest border-2 border-[#00FF41] bg-[#00FF41]/10 hover:bg-[#00FF41]/20 hover:shadow-[0_0_20px_rgba(0,255,65,0.4)] transition-all"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            FLY AGAIN
          </button>
          <button
            onClick={onBackToArcade}
            className="flex-1 py-3 font-bold tracking-widest border border-[#00FF4140] bg-black/30 hover:border-[#00FF4180] transition-all text-sm"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            BACK TO ARCADE
          </button>
        </div>
      </div>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-[#00FF4130] bg-black/30 p-3 text-center">
      <div className="text-[10px] tracking-widest opacity-60">{label}</div>
      <div
        className="text-2xl font-bold mt-1"
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {value}
      </div>
    </div>
  )
}
